import React from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/** "Brandon Simmonds" -> "BS", "Brandon" -> "B", "Brandon Van Dyke" -> "BV" (up to 2). */
export function getInitials(name) {
  if (!name || typeof name !== 'string') return 'B';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'B';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

export function resolveAvatarUrl(url) {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return BACKEND_URL + url;
}

/**
 * Circular avatar — shows uploaded photo if present, otherwise initials.
 * Used across Profile, Squad, Compare, RoutineCard.
 */
export default function Avatar({
  user,
  displayName,
  avatarUrl,
  size = 40,
  className = '',
  tone = 'auto', // 'auto' | 'graphite' | 'red' | 'blue'
  testId,
}) {
  const name = displayName || user?.display_name || '';
  const url = resolveAvatarUrl(avatarUrl ?? user?.avatar_url);
  const initials = getInitials(name);

  const dim = { width: size, height: size };
  const fontSize = Math.max(11, Math.round(size * 0.4));

  const bgByTone = {
    graphite: 'bg-graphite text-bone',
    red: 'bg-userRed text-bone',
    blue: 'bg-partnerBlue text-bone',
    auto: 'bg-graphite/10 text-graphite border border-hairline',
  };
  const toneCls = bgByTone[tone] || bgByTone.auto;

  return (
    <span
      className={`inline-flex items-center justify-center rounded-full overflow-hidden shadow-ios flex-shrink-0 ${toneCls} ${className}`}
      style={dim}
      data-testid={testId || 'avatar'}
      aria-label={name || 'User'}
    >
      {url ? (
        <img
          src={url}
          alt=""
          className="w-full h-full object-cover"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
          data-testid={testId ? `${testId}-image` : 'avatar-image'}
        />
      ) : (
        <span
          className="font-black font-heading uppercase tracking-tight leading-none"
          style={{ fontSize }}
          data-testid={testId ? `${testId}-initials` : 'avatar-initials'}
        >
          {initials}
        </span>
      )}
    </span>
  );
}
